<template>
  <div class="context-menu">
    <!-- Add To Canned Responses -->
    <woot-modal
      v-if="isCannedResponseModalOpen && enabledOptions['cannedResponse']"
      :show.sync="isCannedResponseModalOpen"
      :on-close="hideCannedResponseModal"
    >
      <add-canned-modal
        :response-content="plainTextContent"
        :on-close="hideCannedResponseModal"
      />
    </woot-modal>
    <!-- Translate Content -->
    <translate-modal
      v-if="showTranslateModal"
      :content="messageContent"
      :content-attributes="contentAttributes"
      @close="onCloseTranslateModal"
    />
    <!-- Forward Content -->
    <forward-modal
      v-if="showForwardModal"
      :message="message"
      @close="onCloseForwardModal"
    />
    <!-- Hide Content -->
    <hide-message-modal
      v-if="showHideMessageModal"
      :message="message"
      @close="onCloseHideMessageModal"
    />
    <woot-button
      icon="more-vertical"
      color-scheme="secondary"
      variant="clear"
      size="small"
      @click="handleOpen"
    />
    <woot-context-menu
      v-if="isOpen && !isCannedResponseModalOpen"
      :x="contextMenuPosition.x"
      :y="contextMenuPosition.y"
      @close="handleClose"
      v-clickaway="handleClose"
    >
      <div class="menu-container">
        <menu-item
          :option="{
            icon: 'arrow-reply',
            label: $t('CONVERSATION.CONTEXT_MENU.REPLY_MESSAGE'),
          }"
          variant="icon"
          @click="handleReplyMessage"
        />
        <menu-item
          v-if="enabledOptions['copy']"
          :option="{
            icon: 'clipboard',
            label: this.$t('CONVERSATION.CONTEXT_MENU.COPY'),
          }"
          variant="icon"
          @click="handleCopy"
        />
        <menu-item
          v-if="enabledOptions['copy']"
          :option="{
            icon: 'translate',
            label: this.$t('CONVERSATION.CONTEXT_MENU.TRANSLATE'),
          }"
          variant="icon"
          @click="handleTranslate"
        />
        <menu-item
          :option="{
            icon: 'share',
            label: 'Encaminhar',
          }"
          variant="icon"
          @click="handleForward"
        />
        <menu-item
          v-if="isHidden"
          :option="{
            icon: 'eye-show',
            label: 'Desocultar',
          }"
          variant="icon"
          @click="handleShowMessage"
        />
        <menu-item
          v-else
          :option="{
            icon: 'eye-hide',
            label: 'Ocultar',
          }"
          variant="icon"
          @click="handleHideMessage"
        />
        <menu-item
          v-if="isImageMessage"
          :option="{
            icon: 'preview-link',
            label: 'Abrir imagem em nova guia',
          }"
          variant="icon"
          @click="handleOpenImageInNewTab"
        />
        <hr />
        <menu-item
          :option="{
            icon: 'link',
            label: this.$t('CONVERSATION.CONTEXT_MENU.COPY_PERMALINK'),
          }"
          variant="icon"
          @click="copyLinkToMessage"
        />
        <menu-item
          v-if="enabledOptions['cannedResponse'] && canCreateShortcut"
          :option="{
            icon: 'comment-add',
            label: this.$t(
              'CONVERSATION.CONTEXT_MENU.CREATE_A_CANNED_RESPONSE'
            ),
          }"
          variant="icon"
          @click="showCannedResponseModal"
        />
      </div>
    </woot-context-menu>
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import { mapGetters } from 'vuex';
import { mixin as clickaway } from 'vue-clickaway';
import { directive as onClickaway } from 'vue-clickaway';
import messageFormatterMixin from 'shared/mixins/messageFormatterMixin';
import AddCannedModal from 'dashboard/routes/dashboard/settings/canned/AddCanned';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { conversationUrl, frontendURL } from '../../../helper/URLHelper';
import { ACCOUNT_EVENTS } from '../../../helper/AnalyticsHelper/events';
import TranslateModal from 'dashboard/components/widgets/conversation/bubble/TranslateModal';
import MenuItem from '../../../components/widgets/conversation/contextMenu/menuItem.vue';
import ForwardModal from 'dashboard/components/widgets/conversation/bubble/ForwardModal';
import HideMessageModal from 'dashboard/components/widgets/conversation/bubble/HideMessageModal';
import adminMixin from 'dashboard/mixins/isAdmin';
import agentMixin from 'dashboard/mixins/agentMixin';

export default {
  components: {
    AddCannedModal,
    TranslateModal,
    MenuItem,
    ForwardModal,
    HideMessageModal,
  },
  directives: {
    clickaway: onClickaway,
  },
  mixins: [
    alertMixin,
    clickaway,
    messageFormatterMixin,
    adminMixin,
    agentMixin,
  ],
  props: {
    message: {
      type: Object,
      required: true,
    },
    isOpen: {
      type: Boolean,
      default: false,
    },
    enabledOptions: {
      type: Object,
      default: () => ({}),
    },
    contextMenuPosition: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isCannedResponseModalOpen: false,
      showTranslateModal: false,
      showForwardModal: false,
      showHideMessageModal: false,
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      currentAccountId: 'getCurrentAccountId',
      currentUser: 'getCurrentUser',
      currentRole: 'getCurrentRole',
    }),
    plainTextContent() {
      return this.getPlainText(this.messageContent);
    },
    conversationId() {
      return this.message.conversation_id;
    },
    messageId() {
      return this.message.id;
    },
    messageContent() {
      return this.message.content;
    },
    contentAttributes() {
      return this.message.content_attributes;
    },
    isHidden() {
      return this.message.hidden;
    },
    isImageMessage() {
      return (
        this.message.attachments &&
        this.message.attachments.length > 0 &&
        this.message.attachments[0].file_type === 'image'
      );
    },
    canCreateShortcut() {
      const canShortcut = this.agentRules?.hasOwnProperty('create_shortcut')
        ? this.agentRules.create_shortcut === true
        : true;

      return (
        canShortcut ||
        this.currentUser?.type === 'SuperAdmin' ||
        this.currentRole === 'administrator' ||
        this.currentRole === 'team_supervisor'
      );
    },
  },
  methods: {
    async copyLinkToMessage() {
      const fullConversationURL =
        window.chatwootConfig.hostURL +
        frontendURL(
          conversationUrl({
            id: this.conversationId,
            accountId: this.currentAccountId,
          })
        );
      await copyTextToClipboard(
        `${fullConversationURL}?messageId=${this.messageId}`
      );
      this.showAlert(this.$t('CONVERSATION.CONTEXT_MENU.LINK_COPIED'));
      this.handleClose();
    },
    async handleCopy() {
      await copyTextToClipboard(this.plainTextContent);
      this.showAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
      this.handleClose();
    },
    showCannedResponseModal() {
      this.$track(ACCOUNT_EVENTS.ADDED_TO_CANNED_RESPONSE);
      this.isCannedResponseModalOpen = true;
    },
    hideCannedResponseModal() {
      this.isCannedResponseModalOpen = false;
      this.handleClose();
    },
    handleOpen(e) {
      if (this.isOpen) {
        this.handleClose();
      } else {
        this.$emit('open', e);
      }
    },
    handleClose(e) {
      this.$emit('close', e);
    },
    handleTranslate() {
      const { locale } = this.getAccount(this.currentAccountId);
      this.$store.dispatch('translateMessage', {
        conversationId: this.conversationId,
        messageId: this.messageId,
        targetLanguage: locale || 'en',
      });
      this.handleClose();
      this.showTranslateModal = true;
    },
    onCloseTranslateModal() {
      this.showTranslateModal = false;
    },
    handleForward() {
      this.handleClose();
      this.showForwardModal = true;
    },
    onCloseForwardModal() {
      this.showForwardModal = false;
    },
    handleHideMessage() {
      this.handleClose();
      this.showHideMessageModal = true;
    },
    onCloseHideMessageModal() {
      this.showHideMessageModal = false;
    },
    handleShowMessage() {
      this.$store.dispatch('showMessage', {
        conversationId: this.conversationId,
        messageId: this.messageId,
      });
      this.showAlert('Essa mensagem foi desocultada.');
      this.handleClose();
    },
    handleReplyMessage() {
      window.bus.$emit('reply-to-message', {
        content: this.messageContent,
        source_id: this.message.source_id,
      });
      this.handleClose();
    },
    handleOpenImageInNewTab() {
      if (
        this.message.attachments &&
        this.message.attachments[0] &&
        this.message.attachments[0].data_url
      ) {
        window.open(this.message.attachments[0].data_url, '_blank');
      }
      this.handleClose();
    },
  },
};
</script>

<style lang="scss" scoped>
.menu-container {
  padding: var(--space-smaller);
  background-color: var(--white);
  box-shadow: var(--shadow-context-menu);
  border-radius: var(--border-radius-normal);

  hr:first-child {
    display: none;
  }

  hr {
    border-bottom: 1px solid var(--color-border-light);
    margin: var(--space-smaller);
  }
}
</style>
